// PATH: src/pages/Albums.jsx
// VERSION: 3.8 — Carrusel + filtros por tags multiselect (dropdown en md- / chips en lg+)

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Container,
  Heading,
  SimpleGrid,
  Image,
  Text,
  Badge,
  HStack,
  Tag,
  TagLabel,
  VStack,
  Button,
  Input,
  Flex,
  useBreakpointValue,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Wrap,
  WrapItem,
  IconButton,
  AspectRatio,
  Spinner,
  Portal,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Checkbox,
  useColorModeValue,
  Skeleton,
} from '@chakra-ui/react';
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  CloseIcon,
  ChevronDownIcon,
} from '@chakra-ui/icons';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { cld } from '../utils/cloudinary';
import { API } from '../utils/apiBase';

const PAGE_SIZE = 6;
const PLACEHOLDER_IMG = 'https://via.placeholder.com/800x600?text=Imagen';
const PLACEHOLDER_THUMB = 'https://via.placeholder.com/320x180?text=Media';

/* -------------------- fetch utils -------------------- */
async function fetchJson(url) {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}
function normalizeAlbumsPayload(payload) {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.albums)) return payload.albums;
  if (Array.isArray(payload.items)) return payload.items;
  if (Array.isArray(payload.data)) return payload.data;
  return [];
}
function normalizeEventsPayload(payload) {
  if (!payload) return [];
  if (Array.isArray(payload.items)) return payload.items;
  if (Array.isArray(payload.events)) return payload.events;
  if (Array.isArray(payload.data)) return payload.data;
  return [];
}

/* -------------------- media helpers -------------------- */
const isString = v => typeof v === 'string' && v.trim().length > 0;
const isHttp = s => isString(s) && /^https?:\/\//i.test(s);
const isCloudinaryId = s => isString(s) && !isHttp(s);

function normalizeYouTubeToEmbed(url) {
  if (!isString(url)) return '';
  try {
    const u = new URL(url);
    if (u.hostname.includes('youtu.be')) {
      const id = u.pathname.slice(1);
      return `https://www.youtube.com/embed/${id}`;
    }
    if (u.hostname.includes('youtube.com') && u.pathname.startsWith('/shorts/')) {
      const id = u.pathname.split('/')[2];
      return `https://www.youtube.com/embed/${id}`;
    }
    if (u.hostname.includes('youtube.com') && u.pathname === '/watch') {
      const id = u.searchParams.get('v');
      if (id) return `https://www.youtube.com/embed/${id}`;
    }
    if (u.pathname.startsWith('/embed/')) return url;
    return url;
  } catch {
    return url || '';
  }
}
function youtubeThumb(url) {
  if (!isString(url)) return undefined;
  const embed = normalizeYouTubeToEmbed(url);
  const m = embed.match(/\/embed\/([a-zA-Z0-9_-]{6,})/);
  const id = m?.[1];
  return id ? `https://img.youtube.com/vi/${id}/hqdefault.jpg` : undefined;
}
function cloudinaryVideoThumb(publicId, opts = {}) {
  return cld(publicId, {
    resourceType: 'video',
    format: 'jpg',
    so: 1,
    w: opts.w ?? 400,
    h: opts.h ?? 300,
    crop: opts.crop ?? 'fill',
    gravity: opts.gravity ?? 'auto',
  });
}
function imageUrlFromSrc(src, { w = 1600, h = 1000, fit = 'contain' } = {}) {
  if (!isString(src)) return '';
  if (isHttp(src)) return src;

  if (fit === 'cover') {
    return cld(src, { w, h, crop: 'fill', gravity: 'auto' });
  }

  return cld(src, { w, h, crop: 'fit' });
}

/**
 * Normaliza un item aceptando múltiples esquemas
 */
function resolveItemFields(rawIt) {
  const it = rawIt || {};
  const direct = it.src || it.url || it.image || it.publicId || it.videoUrl || '';
  const fallbackSrc = it.thumbnail || '';
  const primarySrc = isString(direct)
    ? direct
    : isString(fallbackSrc)
      ? fallbackSrc
      : '';

  let type = it.type;
  if (!type) {
    const s = (primarySrc || '').toLowerCase();
    if (s.includes('youtube.com') || s.includes('youtu.be')) type = 'video';
    else if (/\.(mp4|webm|ogg)(\?|#|$)/i.test(s)) type = 'video';
    else type = 'image';
  }

  let thumb = it.thumbnail;
  if (!thumb) {
    if (type === 'video') {
      if (primarySrc.includes('youtube.com') || primarySrc.includes('youtu.be')) {
        thumb = youtubeThumb(primarySrc);
      } else if (isCloudinaryId(primarySrc)) {
        thumb = cloudinaryVideoThumb(primarySrc, { w: 320, h: 180 });
      }
    } else {
      if (isCloudinaryId(primarySrc)) {
        thumb = cld(primarySrc, {
          w: 320,
          h: 180,
          crop: 'fill',
          gravity: 'auto',
        });
      }
    }
  }

  return {
    ...it,
    type,
    src: primarySrc,
    thumbnail: thumb,
    _resolved: true,
  };
}

export default function Albums() {
  const [query, setQuery] = useState('');
  const [selectedTags, setSelectedTags] = useState([]); // multiselect de tags
  const [page, setPage] = useState(1);
  const [searchParams] = useSearchParams();

  const [albums, setAlbums] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [imgLoading, setImgLoading] = useState(true);
  const [err, setErr] = useState(null);

  /* carga remota */
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        setErr(null);
        const [alPayload, evPayload] = await Promise.all([
          fetchJson(`${API}/albums?includeItems=1&limit=500`),
          fetchJson(`${API}/events?limit=1000`),
        ]);
        if (!alive) return;

        const normAlbums = normalizeAlbumsPayload(alPayload).map(a => ({
          ...a,
          items: Array.isArray(a.items)
            ? a.items.map(resolveItemFields)
            : [],
        }));

        setAlbums(normAlbums);
        setEvents(normalizeEventsPayload(evPayload));
      } catch (e) {
        if (alive) setErr(e?.message || 'Error al cargar');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const eventsMap = useMemo(() => {
    const m = new Map();
    (events || []).forEach(e => {
      const key = (e?.key || `${e?.date || ''}|${e?.title || ''}`).trim();
      if (key) m.set(key, e);
    });
    return m;
  }, [events]);

  const albumsSorted = useMemo(
    () =>
      (albums || [])
        .slice()
        .sort(
          (a, b) => new Date(b?.date || 0) - new Date(a?.date || 0)
        ),
    [albums]
  );

  const allTags = useMemo(() => {
    const s = new Set();
    albumsSorted.forEach(a => {
      (a.tags || []).forEach(t => s.add(t));
      (a.items || []).forEach(it =>
        (it.tags || []).forEach(t => s.add(t))
      );
    });
    return Array.from(s).sort((a, b) => a.localeCompare(b));
  }, [albumsSorted]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();

    return albumsSorted.filter(a => {
      const albumText = `${a.title ?? ''} ${(a.tags || []).join(
        ' '
      )}`.toLowerCase();
      const itemsText = (a.items || [])
        .map(
          it =>
            `${it.caption || it.title || ''} ${(it.tags || []).join(' ')}`
        )
        .join(' ')
        .toLowerCase();
      const hitQ = !q || albumText.includes(q) || itemsText.includes(q);

      if (!selectedTags.length) {
        return hitQ;
      }

      const albumTags = a.tags || [];
      const itemTags = (a.items || []).flatMap(it => it.tags || []);
      const hitTag = selectedTags.some(
        t =>
          albumTags.includes(t) || itemTags.includes(t)
      );

      return hitQ && hitTag;
    });
  }, [albumsSorted, query, selectedTags]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const start = (page - 1) * PAGE_SIZE;
  const pageItems = filtered.slice(start, start + PAGE_SIZE);

  const albumModal = useDisclosure();
  const [currentAlbum, setCurrentAlbum] = useState(null);
  const openAlbum = a => {
    setCurrentAlbum(a);
    albumModal.onOpen();
  };

  /* deep-link via params */
  useEffect(() => {
    if (!albumsSorted.length) return;
    const fa = searchParams.get('focusAlbumId');
    if (fa) {
      const idxById = albumsSorted.findIndex(
        a => String(a.id) === String(fa)
      );
      if (idxById >= 0) {
        const targetPage = Math.floor(idxById / PAGE_SIZE) + 1;
        setPage(targetPage);
        setTimeout(() => {
          const album = albumsSorted[idxById];
          if (album) openAlbum(album);
        }, 0);
        return;
      }
    }
    const ek = searchParams.get('eventKey');
    if (ek) {
      const idxByEk = albumsSorted.findIndex(
        a => String(a.eventKey) === String(ek)
      );
      if (idxByEk >= 0) {
        const targetPage = Math.floor(idxByEk / PAGE_SIZE) + 1;
        setPage(targetPage);
        setTimeout(() => {
          const album = albumsSorted[idxByEk];
          if (album) openAlbum(album);
        }, 0);
      }
    }
  }, [searchParams, albumsSorted]); // eslint-disable-line react-hooks/exhaustive-deps

  const getLinkedEvent = album => {
    if (!album?.eventKey) return null;
    return eventsMap.get(String(album.eventKey)) || null;
  };

  /* -------------------- LIGHTBOX -------------------- */
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const itemsCount = currentAlbum?.items?.length ?? 0;

  const openLightboxAt = idx => {
    const safeIdx = Math.max(
      0,
      Math.min(
        idx,
        Math.max(0, (currentAlbum?.items?.length ?? 1) - 1)
      )
    );
    setCarouselIndex(safeIdx);
    if (albumModal.isOpen) albumModal.onClose();
    setLightboxOpen(true);
    try {
      document.body.style.overflow = 'hidden';
    } catch { }
  };
  const closeLightbox = () => {
    setLightboxOpen(false);
    try {
      document.body.style.overflow = '';
    } catch { }
  };
  const next = () =>
    setCarouselIndex(i =>
      itemsCount ? (i + 1) % itemsCount : 0
    );
  const prev = () =>
    setCarouselIndex(i =>
      itemsCount ? (i - 1 + itemsCount) % itemsCount : 0
    );

  useEffect(() => {
    if (!currentAlbum) return;
    const n = currentAlbum.items?.length ?? 0;
    if (n === 0) setCarouselIndex(0);
    else if (carouselIndex >= n) setCarouselIndex(n - 1);
  }, [currentAlbum, carouselIndex]);

  useEffect(() => {
    setImgLoading(true);
  }, [carouselIndex]);

  useEffect(() => {
    if (!lightboxOpen) return;
    const onKey = e => {
      if (e.key === 'ArrowRight') next();
      else if (e.key === 'ArrowLeft') prev();
      else if (e.key === 'Escape') closeLightbox();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightboxOpen, itemsCount]);

  const touchStartX = useRef(null);
  const onTouchStart = e => {
    touchStartX.current = e.touches[0].clientX;
  };
  const onTouchEnd = e => {
    if (touchStartX.current == null) return;
    const dx =
      e.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;
    if (Math.abs(dx) > 50) {
      dx < 0 ? next() : prev();
    }
  };

  /** Miniatura para grilla/preview */
  function thumbFor(itRaw) {
    const it = itRaw?._resolved ? itRaw : resolveItemFields(itRaw);
    if (isString(it.thumbnail)) return it.thumbnail;
    return PLACEHOLDER_THUMB;
  }

  /** Stage del lightbox (sin hooks adentro) */
  function renderLightboxItem() {
    const itRaw = currentAlbum?.items?.[carouselIndex];
    const it = itRaw?._resolved ? itRaw : resolveItemFields(itRaw);
    if (!it) {
      return (
        <Box color="whiteAlpha.900" fontWeight="semibold">
          No hay elemento para mostrar
        </Box>
      );
    }

    const type = it.type;
    let src = it.src;

    if (!isString(src)) src = it.thumbnail;

    if (
      type === 'video' &&
      isString(src) &&
      (src.includes('youtube.com') || src.includes('youtu.be'))
    ) {
      const embed = normalizeYouTubeToEmbed(src);
      if (!embed)
        return (
          <Box color="whiteAlpha.900">
            Video de YouTube no válido
          </Box>
        );
      return (
        <Box
          w="100%"
          maxW="min(1200px, 96vw)"
          mx="auto"
        >
          <AspectRatio ratio={16 / 9}>
            <Box
              as="iframe"
              src={embed}
              title={`video-${carouselIndex}`}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              referrerPolicy="strict-origin-when-cross-origin"
              borderRadius="md"
            />
          </AspectRatio>
        </Box>
      );
    }

    if (type === 'video' && isCloudinaryId(src)) {
      const url = cld(src, { resourceType: 'video' });
      return (
        <Box
          w="100%"
          maxW="min(1200px, 96vw)"
          mx="auto"
        >
          <AspectRatio ratio={16 / 9}>
            <Box
              as="video"
              src={url}
              controls
              playsInline
              controlsList="nodownload"
              style={{ width: '100%', height: '100%' }}
            />
          </AspectRatio>
        </Box>
      );
    }

    if (type === 'video' && isHttp(src)) {
      return (
        <Box
          w="100%"
          maxW="min(1200px, 96vw)"
          mx="auto"
        >
          <AspectRatio ratio={16 / 9}>
            <Box
              as="video"
              src={src}
              controls
              playsInline
              controlsList="nodownload"
              style={{ width: '100%', height: '100%' }}
            />
          </AspectRatio>
        </Box>
      );
    }

    const imgSrc =
      imageUrlFromSrc(src, {
        w: 1600,
        h: 1000,
        crop: 'limit',
      }) || PLACEHOLDER_IMG;

    return (
      <Box
        w="100%"
        maxW="min(1200px, 96vw)"
        mx="auto"
        position="relative"
        minH="400px"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        {imgLoading && <Spinner size="xl" color="blue.500" position="absolute" thickness="4px" />}
        <Image
          src={imgSrc}
          alt={
            it.caption ||
            it.title ||
            `img-${carouselIndex}`
          }
          w="100%"
          maxH={{ base: '70vh', md: '80vh' }}
          objectFit="contain"
          borderRadius="md"
          fallbackSrc={PLACEHOLDER_IMG}
          onLoad={() => setImgLoading(false)}
          onError={ev => {
            setImgLoading(false);
            ev.currentTarget.src = PLACEHOLDER_IMG;
          }}
          opacity={imgLoading ? 0 : 1}
          transition="opacity 0.3s"
        />
      </Box>
    );
  }

  const isMdOrBelow = useBreakpointValue({
    base: true,
    md: true,
    lg: false,
  });
  const previewLimit =
    useBreakpointValue({
      base: 4,
      sm: 6,
      md: 9,
      lg: 12,
    }) ?? 6;

  const searchPlaceholder = useBreakpointValue({
    base: 'Buscar…',
    md: 'Buscar por título, tags o captions…',
  });

  const selectedTagsLabel = useMemo(() => {
    if (!selectedTags.length) return 'Todas las etiquetas';
    if (selectedTags.length === 1) return selectedTags[0];
    return `${selectedTags.length} etiquetas seleccionadas`;
  }, [selectedTags]);

  const activeTagBg = useColorModeValue('black', 'white');
  const activeTagColor = useColorModeValue('white', 'black');
  const hiddenBg = useColorModeValue('gray.100', 'whiteAlpha.200');
  const hiddenHoverBg = useColorModeValue('gray.200', 'whiteAlpha.300');

  if (loading) {
    return (
      <Box bg="bg.default" flex="1">
        <Container
          maxW="container.xl"
          py={{ base: 8, md: 12 }}
        >
          <HStack justify="center" py={10}>
            <Spinner size="lg" />
            <Text> Cargando álbumes…</Text>
          </HStack>
        </Container>
      </Box>
    );
  }

  if (err) {
    return (
      <Box bg="bg.default" flex="1">
        <Container
          maxW="container.xl"
          py={{ base: 8, md: 12 }}
        >
          <Heading
            size="md"
            color="red.600"
          >
            Error al cargar álbumes
          </Heading>
          <Text color="gray.700" mt={2}>
            {String(err)}
          </Text>
        </Container>
      </Box>
    );
  }

  return (
    <Box bg="bg.default" flex="1">
      <Container
        maxW="container.xl"
        py={{ base: 8, md: 12 }}
      >
        <Heading
          textAlign="center"
          mb={6}
          color="text.default"
        >
          Álbumes
        </Heading>
        {/* Buscador + filtros por tags */}
        <VStack align="stretch" spacing={4} mb={6}>
          <Input
            borderColor="border.default"
            borderWidth="2px"
            placeholder={searchPlaceholder}
            value={query}
            onChange={e => {
              setPage(1);
              setQuery(e.target.value);
            }}
            bg="bg.card"
            _hover={{ borderColor: 'blue' }}
            _placeholder={{ color: 'text.muted' }}
          />

          {isMdOrBelow ? (
            <Menu closeOnSelect={false}>
              <MenuButton
                as={Button}
                variant="outline"
                rightIcon={<ChevronDownIcon />}
                alignSelf="flex-start"
                borderColor="border.default"
                bg="bg.card"
                _hover={{
                  bg: 'black',
                  color: 'white',
                  borderColor: 'gray.700',
                }}
              >
                {selectedTagsLabel}
              </MenuButton>
              <MenuList maxH="260px" overflowY="auto">
                <MenuItem>
                  <Checkbox
                    isChecked={!selectedTags.length}
                    isIndeterminate={false}
                    onChange={() => {
                      setPage(1);
                      setSelectedTags([]);
                    }}
                  >
                    Todas
                  </Checkbox>
                </MenuItem>
                {allTags.map(tag => (
                  <MenuItem key={tag}>
                    <Checkbox
                      isChecked={selectedTags.includes(tag)}
                      onChange={() => {
                        setPage(1);
                        setSelectedTags(prev =>
                          prev.includes(tag)
                            ? prev.filter(t => t !== tag)
                            : [...prev, tag]
                        );
                      }}
                    >
                      {tag}
                    </Checkbox>
                  </MenuItem>
                ))}
              </MenuList>
            </Menu>
          ) : (
            <HStack spacing={2} overflowX="auto" pb={1}>
              {/* Tag "Todos" */}
              <Tag
                variant="outline"
                bg={!selectedTags.length ? activeTagBg : 'bg.card'}
                color={!selectedTags.length ? activeTagColor : 'text.default'}
                borderColor={!selectedTags.length ? 'gray.700' : 'border.default'}
                borderWidth="2px"
                cursor="pointer"
                boxShadow={
                  !selectedTags.length
                    ? '0 0 0 1px rgba(15,23,42,0.5)'
                    : 'none'
                }
                _hover={
                  !selectedTags.length
                    ? {
                      borderColor: 'gray.500',
                      boxShadow:
                        '0 0 0 1px rgba(15,23,42,0.7)',
                    }
                    : {
                      bg: activeTagBg,
                      color: activeTagColor,
                      borderColor: 'gray.700',
                      boxShadow:
                        '0 0 0 1px rgba(15,23,42,0.5)',
                    }
                }
                onClick={() => {
                  setPage(1);
                  setSelectedTags([]);
                }}
              >
                <TagLabel>Todos</TagLabel>
              </Tag>

              {/* Tags individuales */}
              {allTags.map(tag => {
                const isActive = selectedTags.includes(tag);
                return (
                  <Tag
                    key={tag}
                    variant="outline"
                    bg={isActive ? activeTagBg : 'bg.card'}
                    color={isActive ? activeTagColor : 'text.default'}
                    borderColor={isActive ? 'gray.700' : 'border.default'}
                    borderWidth="2px"
                    cursor="pointer"
                    boxShadow={
                      isActive
                        ? '0 0 0 1px rgba(15,23,42,0.5)'
                        : 'none'
                    }
                    _hover={
                      isActive
                        ? {
                          borderColor: 'gray.500',
                          boxShadow:
                            '0 0 0 1px rgba(15,23,42,0.7)',
                        }
                        : {
                          bg: activeTagBg,
                          color: activeTagColor,
                          borderColor: 'gray.700',
                          boxShadow:
                            '0 0 0 1px rgba(15,23,42,0.4)',
                        }
                    }
                    onClick={() => {
                      setPage(1);
                      setSelectedTags(prev =>
                        prev.includes(tag)
                          ? prev.filter(t => t !== tag)
                          : [...prev, tag]
                      );
                    }}
                  >
                    <TagLabel>{tag}</TagLabel>
                  </Tag>
                );
              })}
            </HStack>
          )}
        </VStack>

        <SimpleGrid
          columns={{ base: 1, md: 2, lg: 3 }}
          spacing={8}
          alignItems="stretch"
        >
          {pageItems.map(a => {
            const linkedEvent = getLinkedEvent(a);
            const visibleTags = (a.tags || []).slice(0, 3);
            const extraCount = Math.max(
              0,
              (a.tags || []).length - visibleTags.length
            );
            return (
              <Box
                key={a.id}
                mb={0}
                bg="bg.card"
                border="1px solid"
                borderColor="border.default"
                borderRadius="xl"
                overflow="hidden"
                boxShadow="sm"
                display="flex"
                flexDirection="column"
                h="100%"
                transition="all .25s ease"
                _hover={{
                  transform: 'translateY(-4px)',
                  boxShadow: 'md',
                }}
              >
                <Image
                  src={
                    /^https?:\/\//i.test(a.cover)
                      ? a.cover
                      : cld(a.cover, {
                        w: 600,
                        h: 400,
                        crop: 'fill',
                        gravity: 'auto',
                      })
                  }
                  alt={a.title}
                  w="100%"
                  h="200px"
                  objectFit="cover"
                  fallbackSrc="https://via.placeholder.com/600x400?text=Portada+álbum"
                  onError={ev => {
                    ev.currentTarget.src =
                      'https://via.placeholder.com/600x400?text=Portada+álbum';
                  }}
                />
                <VStack align="start" spacing={2} p={5}>
                  <Heading
                    size="md"
                    color="text.default"
                    noOfLines={1}
                  >
                    {a.title}
                  </Heading>
                  <Text
                    color="text.muted"
                    fontSize="sm"
                  >
                    {a.date
                      ? new Date(a.date).toLocaleDateString()
                      : ''}
                  </Text>

                  {(a.tags?.length || 0) > 0 && (
                    <Wrap spacing={2}>
                      {visibleTags.map(t => (
                        <WrapItem key={t}>
                          <Badge
                            variant="solid"
                            colorScheme="blue"
                          >
                            {t}
                          </Badge>
                        </WrapItem>
                      ))}
                      {extraCount > 0 && (
                        <WrapItem>
                          <Badge
                            variant="subtle"
                            colorScheme="gray"
                          >
                            +{extraCount}
                          </Badge>
                        </WrapItem>
                      )}
                    </Wrap>
                  )}

                  <Text
                    color="text.default"
                    fontSize="sm"
                  >
                    {(a.items?.length || 0)} elementos
                  </Text>

                  <HStack spacing={2} mt="auto">
                    <Button
                      colorScheme="blue"
                      size="sm"
                      onClick={() => openAlbum(a)}
                    >
                      Ver álbum
                    </Button>
                    {linkedEvent && (
                      <Button
                        as={RouterLink}
                        to={`/proximos-eventos?focusEventKey=${encodeURIComponent(
                          a.eventKey
                        )}`}
                        size="sm"
                        variant="outline"
                        colorScheme="black"
                      >
                        Ver evento
                      </Button>
                    )}
                  </HStack>
                </VStack>
              </Box>
            );
          })}
        </SimpleGrid>

        {/* Paginación */}
        {filtered.length > PAGE_SIZE && (
          <Flex
            mt={8}
            justify="center"
            align="center"
            gap={4}
          >
            <Button
              onClick={() =>
                setPage(p => Math.max(1, p - 1))
              }
              isDisabled={page === 1}
              variant="outline"
              colorScheme="blue"
              leftIcon={<ArrowLeftIcon />}
            >
              Anterior
            </Button>
            <Text color="gray.600">
              Página {page} de {totalPages}
            </Text>
            <Button
              onClick={() =>
                setPage(p =>
                  Math.min(totalPages, p + 1)
                )
              }
              isDisabled={page === totalPages}
              variant="outline"
              colorScheme="blue"
              rightIcon={<ArrowRightIcon />}
            >
              Siguiente
            </Button>
          </Flex>
        )}
      </Container>

      {/* Modal de álbum (preview con thumbs) */}
      {currentAlbum && (
        <Modal
          isOpen={albumModal.isOpen}
          onClose={albumModal.onClose}
          size={{ base: 'full', md: '5xl' }}
          isCentered
          scrollBehavior="inside"
        >
          <ModalOverlay />
          <ModalContent
            bg="bg.card"
            color="text.default"
            maxH="85vh"
          >
            <ModalHeader>{currentAlbum.title}</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <VStack align="start" spacing={4} w="100%">
                {(currentAlbum.tags?.length || 0) > 0 && (
                  <Wrap spacing={2}>
                    {currentAlbum.tags.map(t => (
                      <WrapItem key={t}>
                        <Badge
                          variant="solid"
                          colorScheme="blue"
                        >
                          {t}
                        </Badge>
                      </WrapItem>
                    ))}
                  </Wrap>
                )}

                <SimpleGrid
                  columns={{ base: 2, sm: 3 }}
                  spacing={4}
                  w="100%"
                >
                  {(() => {
                    const total =
                      currentAlbum.items?.length || 0;
                    const show = Math.min(
                      previewLimit,
                      total
                    );
                    const hidden = Math.max(
                      0,
                      total - show
                    );
                    const thumbs = (currentAlbum.items ||
                      []).slice(0, show);

                    return (
                      <>
                        {thumbs.map((it, idx) => {
                          const thumb = thumbFor(it);
                          return (
                            <Box
                              key={idx}
                              bg="bg.card"
                              border="1px solid"
                              borderColor="border.default"
                              borderRadius="md"
                              overflow="hidden"
                              cursor="pointer"
                              onClick={() =>
                                openLightboxAt(idx)
                              }
                              _hover={{
                                boxShadow: 'md',
                                transform:
                                  'translateY(-2px)',
                              }}
                              transition="all .2s ease"
                            >
                              <Image
                                src={
                                  thumb ||
                                  PLACEHOLDER_THUMB
                                }
                                alt={
                                  it.caption ||
                                  it.title ||
                                  `media-${idx}`
                                }
                                w="100%"
                                h="160px"
                                objectFit="cover"
                                fallback={<Skeleton w="100%" h="160px" />}
                                onError={ev => {
                                  ev.currentTarget.src =
                                    PLACEHOLDER_THUMB;
                                }}
                              />
                              {(it.caption ||
                                it.title) && (
                                  <Box p={2}>
                                    <Text
                                      color="text.muted"
                                      fontSize="sm"
                                      noOfLines={2}
                                    >
                                      {it.caption ||
                                        it.title}
                                    </Text>
                                  </Box>
                                )}
                            </Box>
                          );
                        })}

                        {hidden > 0 && (
                          <Box
                            role="button"
                            onClick={() =>
                              openLightboxAt(show)
                            }
                            bg={hiddenBg}
                            border="1px dashed"
                            borderColor="border.default"
                            borderRadius="md"
                            h="160px"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            _hover={{ bg: hiddenHoverBg }}
                          >
                            <Text
                              color="text.default"
                              fontWeight="semibold"
                            >
                              +{hidden} más
                            </Text>
                          </Box>
                        )}
                      </>
                    );
                  })()}
                </SimpleGrid>
              </VStack>
            </ModalBody>
            <ModalFooter>
              <Button
                onClick={() => openLightboxAt(0)}
                colorScheme="blue"
                mr={3}
              >
                Ver carrusel
              </Button>
              <Button
                onClick={albumModal.onClose}
                variant="outline"
              >
                Cerrar
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}

      {/* LIGHTBOX overlay */}
      {currentAlbum && lightboxOpen && (
        <Portal>
          <Box
            position="fixed"
            inset={0}
            bg="rgba(10, 15, 30, 0.96)"
            backdropFilter="blur(2px)"
            zIndex={2200}
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
          >
            <IconButton
              aria-label="Cerrar"
              icon={<CloseIcon />}
              onClick={closeLightbox}
              position="absolute"
              top={{ base: 3, md: 5 }}
              right={{ base: 3, md: 5 }}
              colorScheme="blue"
              variant="solid"
            />

            <Flex
              direction="column"
              h="100%"
              align="center"
              justify="center"
              px={{ base: 2, md: 6 }}
              py={{ base: 6, md: 10 }}
            >
              <HStack
                w="full"
                maxW="min(1200px, 96vw)"
                justify="space-between"
                align="center"
                mb={3}
              >
                <IconButton
                  aria-label="Anterior"
                  icon={<ArrowLeftIcon />}
                  onClick={prev}
                  variant="outline"
                  colorScheme="blue"
                />
                <Box
                  key={`stage-${carouselIndex}`}
                  flex="1"
                  mx={2}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  minH={{
                    base: '50vh',
                    md: '60vh',
                  }}
                  w="full"
                >
                  {renderLightboxItem()}
                </Box>
                <IconButton
                  aria-label="Siguiente"
                  icon={<ArrowRightIcon />}
                  onClick={next}
                  variant="outline"
                  colorScheme="blue"
                />
              </HStack>

              <Box
                mt={2}
                maxW="min(1200px, 96vw)"
                textAlign="center"
              >
                <Text color="white" opacity={0.95}>
                  {currentAlbum.items?.[carouselIndex]
                    ?.caption ||
                    currentAlbum.items?.[carouselIndex]
                      ?.title ||
                    ''}
                </Text>
                <Text
                  color="whiteAlpha.800"
                  fontSize="sm"
                  mt={1}
                >
                  {carouselIndex + 1} / {itemsCount || 0}
                </Text>
              </Box>

              {/* Filmstrip */}
              <HStack
                spacing={2}
                mt={4}
                maxW="min(1200px, 96vw)"
                overflowX="auto"
                py={2}
              >
                {(currentAlbum.items || []).map(
                  (it, idx) => {
                    const srcThumb = thumbFor(it);
                    const active = idx === carouselIndex;
                    return (
                      <Box
                        key={`thumb-${idx}`}
                        as="button"
                        onClick={() =>
                          setCarouselIndex(idx)
                        }
                        border="2px solid"
                        borderColor={
                          active
                            ? 'blue.300'
                            : 'whiteAlpha.400'
                        }
                        borderRadius="md"
                        overflow="hidden"
                        flex="0 0 auto"
                        _hover={{
                          borderColor: active
                            ? 'blue.200'
                            : 'whiteAlpha.600',
                        }}
                      >
                        <Image
                          src={
                            srcThumb || PLACEHOLDER_THUMB
                          }
                          alt={
                            it.caption ||
                            it.title ||
                            `thumb-${idx}`
                          }
                          w={{ base: '100px', md: '140px' }}
                          h={{ base: '64px', md: '84px' }}
                          objectFit="cover"
                          fallbackSrc={
                            PLACEHOLDER_THUMB
                          }
                          onError={ev => {
                            ev.currentTarget.src =
                              PLACEHOLDER_THUMB;
                          }}
                        />
                      </Box>
                    );
                  }
                )}
              </HStack>
            </Flex>
          </Box>
        </Portal>
      )}
    </Box>
  );
}
